/**
 * ec-product.js
 *
 * PDP utility module — wires up:
 *   - quantity stepper (+/-) within [data-ec-quantity]
 *   - add-to-cart AJAX submit on [data-ec-product-form]
 *   - zoom dialog (native <dialog>) opened from the gallery zoom button
 *   - product recommendations lazy fetch via Section Rendering API
 *   - delegated <ec-size-guide-modal> trigger handling
 *
 * No Shadow DOM, light scope only. Idempotent: safe to re-run after a
 * theme editor section morph.
 */

import { CartAddEvent } from '@theme/events';

(function () {
  if (window.__ecProductInit) return;
  window.__ecProductInit = true;

  const root = document;

  // --------------------------------------------------------------
  // Quantity stepper
  // --------------------------------------------------------------
  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const decBtn = target.closest('[data-ec-quantity-decrease]');
    const incBtn = target.closest('[data-ec-quantity-increase]');
    if (!decBtn && !incBtn) return;

    const wrapper = (decBtn || incBtn).closest('[data-ec-quantity]');
    if (!wrapper) return;
    const input = wrapper.querySelector('[data-ec-quantity-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const min = Number.parseInt(input.min || '1', 10);
    const current = Number.parseInt(input.value || '1', 10) || min;
    const next = decBtn ? Math.max(min, current - 1) : current + 1;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // --------------------------------------------------------------
  // Add-to-cart submit (AJAX) — falls back to native POST when fetch
  // is unavailable. Dispatches a standard CartAddEvent (shared with
  // Horizon's product-form.js); <ec-cart-drawer> picks it up to refresh
  // and auto-open, and <ec-header> updates the badge.
  // --------------------------------------------------------------
  root.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-ec-product-form]')) return;

    event.preventDefault();
    const button = form.querySelector('[data-ec-add-to-cart]');
    if (button instanceof HTMLButtonElement) button.disabled = true;

    try {
      const formData = new FormData(form);
      formData.set('sections_url', window.location.pathname);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      const payload = await res.json();
      const drawer = document.querySelector('ec-cart-drawer');

      // Standard CartAddEvent so the rest of the page can react. Resource
      // is left empty — /cart/add.js returns the line item, not the cart,
      // so subscribers refetch /cart.js when they need a full snapshot.
      const cartEvent = new CartAddEvent({}, 'ec-product-form', {
        source: 'ec-product',
        productId: form.dataset.productId,
        variantId: String(formData.get('id') || ''),
        itemCount: Number(formData.get('quantity') || '1'),
      });
      document.dispatchEvent(cartEvent);

      // Fallback for themes without the drawer mounted: keep the legacy
      // navigation so the user still gets feedback that the item landed.
      if (!drawer) {
        window.location.href = '/cart';
      }

      void payload; // payload kept available for future enrichment
    } catch (err) {
      console.error('[ec-product] add-to-cart failed', err);
      document.dispatchEvent(
        new CustomEvent('ec:cart:error', {
          bubbles: true,
          detail: { error: err, source: 'ec-product' },
        })
      );
    } finally {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  });

  // --------------------------------------------------------------
  // Zoom dialog (lightbox)
  // Listens for `ec:zoom:open` events bubbling from the gallery and
  // shows the shared <dialog data-ec-zoom-dialog> with the requested image.
  // --------------------------------------------------------------
  // URLs already streamed at full resolution — avoids re-fetching the HD
  // variant every time the user re-opens the dialog on the same image.
  const highResLoaded = new Set();

  document.addEventListener('ec:zoom:open', (event) => {
    const dialog = document.querySelector('[data-ec-zoom-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    const img = dialog.querySelector('[data-ec-zoom-img]');
    const detail = (event && event.detail) || {};
    if (img instanceof HTMLImageElement) {
      // Reset any zombie state from a previous open: a leftover srcset
      // could let the picker re-pick a different candidate, and removing
      // sizes keeps the resolution decision driven by the explicit src.
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');

      // Step 1 — paint the low-res variant immediately. It's already in
      // the HTTP cache (the gallery rendered the very same URL), so this
      // is a synchronous bitmap swap; no spinner, no blank frame.
      const lowResSrc = detail.src || '';
      const highResSrc = detail.highResSrc || '';
      if (lowResSrc) img.src = lowResSrc;
      img.alt = detail.alt || '';

      // Step 2 — kick off the HD download in the background. When it
      // finishes, swap the dialog <img> src. If it's already cached
      // (re-open of the same image), the swap is instant.
      if (highResSrc && highResSrc !== lowResSrc) {
        if (highResLoaded.has(highResSrc)) {
          img.src = highResSrc;
        } else {
          const hd = new Image();
          hd.onload = () => {
            highResLoaded.add(highResSrc);
            // Only apply if the user hasn't navigated to another image
            // in the meantime (compare against the captured highResSrc).
            if (img.dataset.pendingHighRes === highResSrc) {
              img.src = highResSrc;
              delete img.dataset.pendingHighRes;
            }
          };
          img.dataset.pendingHighRes = highResSrc;
          hd.src = highResSrc;
        }
      }
    }
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  });

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-ec-zoom-close]')) {
      const dialog = target.closest('dialog');
      if (dialog instanceof HTMLDialogElement) dialog.close();
      return;
    }

    // Click backdrop closes (since the <dialog> fills the viewport, only
    // the backdrop area registers as direct target on the dialog itself).
    if (target instanceof HTMLDialogElement && target.matches('[data-ec-zoom-dialog]')) {
      target.close();
    }
  });

  // --------------------------------------------------------------
  // Size guide modal trigger (delegated, since the link sits inside
  // <ec-variant-picker> which sets [aria-disabled] on labels).
  // --------------------------------------------------------------
  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-ec-size-guide-trigger]');
    if (!trigger) return;
    event.preventDefault();
    const modal = document.querySelector('ec-size-guide-modal');
    if (!modal) return;
    const fetchUrl = trigger.getAttribute('data-ec-fetch-url') || '';
    if (typeof modal.open === 'function') {
      modal.open({ fetchUrl, previousFocus: trigger });
    }
  });

  // --------------------------------------------------------------
  // Product recommendations — lazy fetch via Section Rendering API.
  // The section renders an empty placeholder server-side; the client
  // requests the populated section in idle time and morphs the markup.
  // --------------------------------------------------------------
  const recsContainers = document.querySelectorAll('[data-ec-recommendations]');
  if (recsContainers.length > 0) {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    idle(() => {
      recsContainers.forEach(loadRecommendations);
    });
  }

  async function loadRecommendations(container) {
    if (!(container instanceof Element)) return;
    if (container.dataset.ecRecsLoaded === 'true') return;
    if (container.querySelector('.ec-product-recommendations__grid')) {
      // Server-rendered already (recommendations.performed).
      container.dataset.ecRecsLoaded = 'true';
      return;
    }

    const productId = container.getAttribute('data-product-id');
    const intent = container.getAttribute('data-intent') || 'related';
    const limit = container.getAttribute('data-limit') || '4';
    const section =
      container.closest('[data-section-id]')?.getAttribute('data-section-id') ||
      container.closest('.shopify-section')?.id?.replace('shopify-section-', '') ||
      '';

    if (!productId) return;

    const url = new URL('/recommendations/products', window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('intent', intent);
    url.searchParams.set('limit', limit);
    if (section) url.searchParams.set('section_id', section);

    try {
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-ec-recommendations]');
      if (fresh) {
        container.replaceWith(fresh);
        fresh.dataset.ecRecsLoaded = 'true';
      } else {
        container.dataset.ecRecsLoaded = 'true';
      }
    } catch (err) {
      console.error('[ec-product] recommendations fetch failed', err);
    }
  }
})();
